import { useEffect, useRef } from 'react';
import { useTranslations } from '../context/TranslationContext';
import { Header } from '../components/Header';
import { useReveal } from '../hooks/useReveal';
import { SeoHead } from '../components/SeoHead';
import { toArticleLang } from '../services/articles';
import { getPrivacyMeta, privacyPath, homePath, buildBreadcrumbSchema } from '../services/seo';

/**
 * Section order. Kept as data so the three locale files stay the single
 * source of truth for both the wording and which sections exist.
 */
const SECTIONS = [
  'controller',
  'dataCollection',
  'legalBasis',
  'cookies',
  'dataUsage',
  'processors',
  'dataSecurity',
  'retention',
  'gdprRights',
  'contact',
] as const;

export const PrivacyPolicy = () => {
  const { t, lang } = useTranslations();
  const seoLang = toArticleLang(lang);
  const { title, description } = getPrivacyMeta(seoLang, t);
  const mainRef = useRef<HTMLDivElement>(null);
  const privacy = t.page.footer.privacy;
  // Sections are looked up by key so the locale files stay the source of truth
  // for which ones exist; `privacy` also holds two plain strings (title,
  // lastUpdated), hence the narrow cast at the point of use rather than on the
  // whole object.
  const sectionOf = (key: string) =>
    (privacy as unknown as Record<string, { title: string; content: string } | string>)[key];

  useReveal(mainRef);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <div className="min-h-screen bg-cream animate-fadeIn" ref={mainRef}>
      <SeoHead
        lang={seoLang}
        title={title}
        description={description}
        path={privacyPath(seoLang)}
        alternates={{ ro: privacyPath('ro'), hu: privacyPath('hu'), en: privacyPath('en') }}
        jsonLd={[
          buildBreadcrumbSchema([
            { name: t.page.navigation.home, path: homePath(seoLang) },
            { name: privacy.title, path: privacyPath(seoLang) },
          ]),
        ]}
      />
      <Header variant="page" />

      <div className="pt-40 pb-16 px-8 lg:px-16 max-w-7xl mx-auto">
        <div className="max-w-3xl mb-20" data-reveal>
          <div className="eyebrow mb-6">{privacy.lastUpdated}</div>
          <h1 className="display text-[48px] md:text-[64px]">{privacy.title}</h1>
        </div>

        <div className="max-w-3xl">
          {SECTIONS.map((key, index) => {
            const section = sectionOf(key);
            if (!section || typeof section === 'string') return null;
            return (
              <section
                key={key}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-12 border-t border-hair"
                data-reveal
                data-reveal-delay={index % 2 === 1 ? '100' : undefined}
              >
                <div className="lg:col-span-4">
                  <span className="number-tag">{String(index + 1).padStart(2, '0')}</span>
                </div>
                <div className="lg:col-span-8">
                  <h2 className="display text-[26px] md:text-[30px] mb-4">{section.title}</h2>
                  <p className="text-mute text-[15px] leading-[1.9] font-light">{section.content}</p>
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
